import { useRouter } from 'next/router';
import { NavigateFunction } from '../interfaces/navigate-function';
import { To } from '../interfaces/to';
import { useCallback } from 'react';
import { NavigateOptions } from '../interfaces/navigate-options';

/**
 * Custom hook to provide a navigate function using Next.js router.
 * This hook uses the `useRouter` hook from Next.js to access the router's `back` and `push` methods.
 *
 * @returns {NavigateFunction} - Returns a navigate function that can be used to navigate to a specified route or go back in the history.
 *
 * @example
 * const navigate = useNextNavigate();
 * navigate('/home'); // Navigates to the '/home' route
 * navigate(-1); // Goes back in the history
 * navigate('/about', { preventScrollReset: true }); // Navigates to the '/about' route without resetting the scroll position
 */
export function useNextNavigate(): NavigateFunction {
  // Destructure the back and push methods from the useRouter hook
  const { back, push } = useRouter();

  /**
   * Navigate function to handle navigation based on the provided parameters.
   *
   * @param {To | -1} toOrBack - The target route to navigate to or -1 to go back in the history.
   * @param {NavigateOptions} [options] - Optional navigation options.
   */
  const navigate: NavigateFunction = useCallback(
    (toOrBack: To | -1, options?: NavigateOptions) => {
      // If the parameter is -1, go back in the history
      if (toOrBack === -1) {
        back();
        return;
      }

      // If preventScrollReset option is provided, navigate without resetting the scroll position
      if (options?.preventScrollReset) {
        push(toOrBack, undefined, { scroll: false, shallow: true });
        return;
      }

      // Otherwise, navigate to the specified route
      push(toOrBack, undefined, { shallow: options?.shallow });
    },
    [back, push], // Dependencies for the useCallback hook
  );

  // Return the navigate function
  return navigate;
}
